(() => {
  if (customElements.get('bundle-offers')) return;

  const lockedDialogs = new Set();
  let previousOverflow = null;

  const lockScroll = (dialog) => {
    if (lockedDialogs.has(dialog)) return;

    if (lockedDialogs.size === 0) {
      const style = document.documentElement.style;
      previousOverflow = {
        value: style.getPropertyValue('overflow'),
        priority: style.getPropertyPriority('overflow'),
      };
      style.setProperty('overflow', 'hidden', 'important');
    }

    lockedDialogs.add(dialog);
  };

  const unlockScroll = (dialog) => {
    if (!lockedDialogs.delete(dialog) || lockedDialogs.size !== 0) return;

    const style = document.documentElement.style;
    if (previousOverflow.value) {
      style.setProperty('overflow', previousOverflow.value, previousOverflow.priority);
    } else {
      style.removeProperty('overflow');
    }
    previousOverflow = null;
  };

  class BundleOffers extends HTMLElement {
    connectedCallback() {
      if (this.eventController) return;

      this.eventController = new AbortController();
      this.dialogStates = new Map();
      this.activeDialog = null;
      this.backdropPress = null;

      const options = { signal: this.eventController.signal };
      this.addEventListener('click', (event) => this.handleClick(event), options);
      this.addEventListener('pointerdown', (event) => this.handlePointerDown(event), options);
      this.addEventListener('pointercancel', () => { this.backdropPress = null; }, options);
      this.addEventListener('close', (event) => {
        if (event.target.matches('[data-bundle-dialog]') && !event.target.open) {
          this.finishClose(event.target);
        }
      }, { ...options, capture: true });

      document.addEventListener('shopify:block:select', (event) => {
        const dialog = this.findBlockDialog(event.detail?.blockId);
        if (dialog) this.openDialog(dialog, this.findOpener(dialog), false);
      }, options);
      document.addEventListener('shopify:block:deselect', (event) => {
        const dialog = this.findBlockDialog(event.detail?.blockId);
        if (dialog) this.closeDialog(dialog, false);
      }, options);
    }

    disconnectedCallback() {
      if (!this.eventController) return;

      for (const dialog of this.dialogStates.keys()) this.closeDialog(dialog, false);
      this.eventController.abort();
      this.eventController = null;
      this.backdropPress = null;
      this.activeDialog = null;
    }

    findOpener(dialog) {
      return Array.from(this.querySelectorAll('[data-bundle-open]'))
        .find((button) => button.getAttribute('aria-controls') === dialog.id);
    }

    findBlockDialog(blockId) {
      if (!blockId) return null;

      return Array.from(this.querySelectorAll('[data-bundle-dialog]'))
        .find((dialog) => dialog.dataset.blockId === blockId && this.findOpener(dialog));
    }

    isOutsideDialog(event, dialog) {
      const bounds = dialog.getBoundingClientRect();
      return event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom;
    }

    handlePointerDown(event) {
      this.backdropPress = null;
      if (event.button !== 0 || !(event.target instanceof Element)) return;

      const dialog = event.target.closest('[data-bundle-dialog]');
      if (dialog?.open && event.target === dialog && this.isOutsideDialog(event, dialog)) {
        this.backdropPress = { dialog, pointerId: event.pointerId };
      }
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      if (this.scrollToSectionLink(event)) {
        this.backdropPress = null;
        return;
      }

      const closeButton = event.target.closest('[data-bundle-close]');
      if (closeButton && closeButton.closest('bundle-offers') === this) {
        const dialog = closeButton.closest('[data-bundle-dialog]');
        if (dialog) {
          event.preventDefault();
          this.closeDialog(dialog);
        }
        this.backdropPress = null;
        return;
      }

      const openButton = event.target.closest('[data-bundle-open]');
      if (openButton && openButton.closest('bundle-offers') === this) {
        const dialog = document.getElementById(openButton.getAttribute('aria-controls'));
        if (dialog && this.contains(dialog) && dialog.matches('[data-bundle-dialog]')) {
          event.preventDefault();
          this.openDialog(dialog, openButton);
        }
        this.backdropPress = null;
        return;
      }

      const press = this.backdropPress;
      this.backdropPress = null;
      if (press && event.target === press.dialog && this.isOutsideDialog(event, press.dialog)
        && (event.pointerId === undefined || event.pointerId === press.pointerId)) {
        this.closeDialog(press.dialog);
      }
    }

    scrollToSectionLink(event) {
      if (event.defaultPrevented || event.button > 0
        || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

      const link = event.target.closest('.bundle-offers__media[href], .bundle-offers__shop[href]');
      if (!link || link.closest('bundle-offers') !== this || link.hasAttribute('download')
        || (link.target && link.target.toLowerCase() !== '_self')) return false;

      let url;
      let target;
      try {
        url = new URL(link.href, document.baseURI);
        if (!url.hash || url.origin !== window.location.origin
          || url.pathname !== window.location.pathname || url.search !== window.location.search) return false;

        target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      } catch (error) {
        return false;
      }
      if (!target) return false;

      event.preventDefault();
      event.stopPropagation();
      if (window.location.hash !== url.hash) {
        try {
          window.history.pushState(window.history.state, '', url.hash);
        } catch (error) {
          // Keep scrolling functional in embedded previews that restrict history changes.
        }
      }
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
      return true;
    }

    openDialog(dialog, opener, restoreFocus = true) {
      if (dialog.open || !dialog.isConnected) return;
      if (this.activeDialog) this.closeDialog(this.activeDialog, false);

      const state = {
        opener: opener || document.activeElement,
        restoreFocus,
      };
      this.dialogStates.set(dialog, state);
      this.activeDialog = dialog;

      try {
        dialog.showModal();
      } catch (error) {
        this.dialogStates.delete(dialog);
        this.activeDialog = null;
        return;
      }

      opener?.setAttribute('aria-expanded', 'true');
      lockScroll(dialog);
    }

    closeDialog(dialog, restoreFocus = true) {
      const state = this.dialogStates.get(dialog);
      if (state) state.restoreFocus = state.restoreFocus && restoreFocus;
      if (dialog.open) dialog.close();
      this.finishClose(dialog);
    }

    finishClose(dialog) {
      const state = this.dialogStates.get(dialog);
      if (!state) return;

      this.dialogStates.delete(dialog);
      if (this.activeDialog === dialog) this.activeDialog = null;
      if (this.backdropPress?.dialog === dialog) this.backdropPress = null;
      this.findOpener(dialog)?.setAttribute('aria-expanded', 'false');
      unlockScroll(dialog);

      if (state.restoreFocus && state.opener?.isConnected && this.isConnected) {
        state.opener.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('bundle-offers', BundleOffers);
})();
